/**
 * The *tick* half of a rendered frame — everything that decides what the
 * simulation does, up to and including running it.
 *
 * `scene-loop.ts`'s `advanceSim` already named this seam in its own doc —
 * *"everything here is about the **tick**, and everything left in `onFrame`
 * after it is about the **picture**"* — and G44's autopilot is what made it
 * worth being a module: choosing the stick is now a decision with two possible
 * sources, and it belongs on the tick side of that line rather than threaded
 * through the frame body.
 *
 * The order is the contract, and it is not free to change:
 *
 * 1. **Time compression** reconciles first, because the size of this frame's
 *    step depends on it.
 * 2. **The autopilot** follows, because whether the AI has the aeroplane is a
 *    consequence of (1) and nothing else.
 * 3. **The stick is read regardless**, even when the AI is flying — the
 *    keyboard carries state of its own (the throttle lever travels while a key
 *    is held), so skipping the read would hand the player back an aeroplane
 *    flying on a stale lever.
 * 4. **The bail-out hold** advances on the real keys, never the autopilot's, so
 *    a pilot can always go over the side.
 */

import type { FlightInputs } from "@red-opus/engine";
import { autopilotInputs, autopilotModeFor, reconcileAutopilot } from "./autopilot.ts";
import { advanceBailHold, bailHoldCompleted } from "./bail-key.ts";
import { PLAYER_ID } from "./encounter-setup.ts";
import type { FlightFrame } from "./sim-runner.ts";
import { nearestHostileAircraftM } from "./frame-signals.ts";
import type { FlightSceneCallbacks } from "./flight-scene-api.ts";
import type { PadFrame } from "./gamepad-axes.ts";
import type { PointerFrame } from "./pointer.ts";
import { routeWheelForFlyByZoom } from "./scene-flyby-zoom.ts";
import { readFlightInputs } from "./scene-inputs.ts";
import type { SceneState } from "./scene-loop.ts";
import { reconcileTimeCompressionFor, timeCompressionStep } from "./time-compression.ts";

/**
 * AD-021's hold guard, one frame of it.
 *
 * The whole verb, and it is deliberately three lines: `advanceBailHold` is the
 * pure accumulator (`keys.ts`), `bailHoldCompleted` is the rising edge, and the
 * engine's `bailOut` refuses everything that is not a legal jump. Nothing here
 * decides whether the pilot *may* go — that would be a second copy of rules the
 * engine already owns.
 *
 * With `bailEnabled` off the accumulator is not even run, so a player who has
 * switched the feature off cannot half-arm it by resting a finger on `H`.
 */
function advanceBailOut(state: SceneState, pressed: ReadonlySet<string>, dt: number): void {
  if (!state.bailEnabled) {
    state.bailHeldSeconds = 0;
    return;
  }
  const previous = state.bailHeldSeconds;
  state.bailHeldSeconds = advanceBailHold(previous, pressed, dt);
  if (bailHoldCompleted(previous, state.bailHeldSeconds)) state.runner.bailOut?.();
}

/**
 * G44: while time compression is running, the aeroplane is flown by the same AI
 * that flies every other machine in the sky, and the original's own two banners
 * say which way. Returns the stick to fly with, or `null` when the player has
 * the controls.
 *
 * Gated on `allowTimeCompression` rather than on a flag of its own, so `/duel`
 * (which transmits sticks and would desync on tick one) and `/vcr` (whose tape
 * flies itself) are excluded by construction.
 */
function advanceAutopilot(state: SceneState, callbacks: FlightSceneCallbacks): FlightInputs | null {
  if (!state.allowTimeCompression) return null;
  const encounter = state.runner.encounter();
  const reconciled = reconcileAutopilot(state.autopilot, state.timeCompression.active, autopilotModeFor(encounter, PLAYER_ID));
  state.autopilot = reconciled.state;
  if (reconciled.message) callbacks.onMessages([reconciled.message]);
  return reconciled.state.engaged ? autopilotInputs(encounter, PLAYER_ID, state.runner.state()) : null;
}

/**
 * Runs this frame's sim ticks and drains what they produced — the recorder
 * hook, the message line and the effect spawns.
 */
function advanceSim(
  state: SceneState,
  callbacks: FlightSceneCallbacks,
  step: { readonly frameSeconds: number; readonly maxSteps: number },
  inputs: FlightInputs,
): FlightFrame {
  const frame = state.runner.advance(step.frameSeconds, inputs, step.maxSteps);
  // The Mission Recorder's whole hook into the flight loop: every tick of this
  // frame was flown with `inputs`, so a recorder stores one run per change of
  // stick rather than one entry per tick. The autopilot's stick goes on the
  // tape exactly like the player's, which is what a replay needs — it must fly
  // what was flown, not what was pressed.
  if (frame.steps > 0) callbacks.onFlown?.(inputs, frame.steps, state.runner.encounter());
  if (frame.messages.length > 0) {
    callbacks.onMessages(frame.messages);
    state.audio.handleMessages(frame.messages);
  }
  for (const spawn of frame.effects) state.sprites.spawn(spawn);
  return frame;
}

export interface TickInput {
  readonly dt: number;
  readonly pressed: ReadonlySet<string>;
  readonly pointer: PointerFrame;
  readonly pad: PadFrame;
}

export interface TickResult {
  readonly frame: FlightFrame;
  /** The stick the sim was actually flown with this frame — the player's, or the autopilot's. */
  readonly inputs: FlightInputs;
}

export function advanceTick(state: SceneState, callbacks: FlightSceneCallbacks, input: TickInput): TickResult {
  const { dt, pressed, pointer, pad } = input;
  // Hostiles only. The side-blind reading refused compression at spawn on seven
  // catalogue missions because of the player's own formation —
  // `frame-signals.ts` carries the census.
  reconcileTimeCompressionFor(state, nearestHostileAircraftM(state.runner.encounter(), PLAYER_ID), callbacks);
  const step = timeCompressionStep(dt, state.timeCompression.active);
  const flying = advanceAutopilot(state, callbacks);
  const stick = readFlightInputs(state, pressed, pointer, dt, routeWheelForFlyByZoom(state, pointer, dt), pad);
  const inputs = flying ?? stick;
  advanceBailOut(state, pressed, dt);
  return { frame: advanceSim(state, callbacks, step, inputs), inputs };
}
